/**
 * API call helper with default error handling
 */

import { ApiClientError } from './client.js';
import { errorDialog } from '../ui/dialogs.js';

/** Map API error codes to unicode icons for the error dialog title */
const ERROR_ICONS: Record<string, string> = {
  // VM lifecycle
  START_FAILED:     '\u26A0\uFE0F',  // warning
  STOP_FAILED:      '\u26A0\uFE0F',
  KILL_FAILED:      '\u2620\uFE0F',  // skull
  PAUSE_FAILED:     '\u23F8\uFE0F',  // pause
  RESUME_FAILED:    '\u23F8\uFE0F',
  VM_NOT_FOUND:     '\uD83D\uDDA5\uFE0F',  // desktop
  VM_NOT_RUNNING:   '\u26D4',        // no entry
  VM_ALREADY_RUNNING: '\u26D4',

  // Auth / permissions
  UNAUTHORIZED:     '\uD83D\uDD12',  // lock
  FORBIDDEN:        '\uD83D\uDEAB',  // prohibited
  INVALID_CREDENTIALS: '\uD83D\uDD12',

  // Validation / input
  VALIDATION_ERROR: '\u2757',        // exclamation
  CONFLICT:         '\u274C',        // cross mark
  DUPLICATE_NAME:   '\u274C',
  NOT_FOUND:        '\uD83D\uDD0D',  // magnifying glass

  // RDP / connectivity
  IP_NOT_FOUND:     '\uD83C\uDF10',  // globe (network issue)
  RDP_NOT_READY:    '\uD83D\uDD0C',  // plug (port not reachable)

  // Server / infra
  INTERNAL_ERROR:   '\uD83D\uDCA5',  // collision
  API_ERROR:        '\u274C',
  UNKNOWN:          '\u2753',        // question mark
};

function iconForCode(code: string): string {
  return ERROR_ICONS[code] || '\u274C';
}

/**
 * Wraps an API promise with default error dialog display.
 * Returns null on error so callers can distinguish from void success:
 *   const result = await apiCall(api.foo(), 'Title');
 *   if (result === null) return;   // for void calls
 *   if (!result) return;           // for calls returning truthy values
 *
 * Auth errors (401) are silently swallowed since the client's onAuthError
 * callback already redirects to login.
 */
export async function apiCall<T>(
  promise: Promise<T>,
  errorTitle?: string,
  onError?: (error: ApiClientError) => void
): Promise<T | null> {
  try {
    return await promise;
  } catch (error) {
    // Auth errors redirect to login via onAuthError — don't double-display
    // unless the caller provided a custom handler (e.g. login page)
    if (error instanceof ApiClientError && error.isAuthError() && !onError) return null;

    if (onError) {
      onError(error instanceof ApiClientError ? error : new ApiClientError('UNKNOWN', String(error)));
    } else {
      const msg = error instanceof Error ? error.message : String(error);
      const code = error instanceof ApiClientError ? error.code : 'UNKNOWN';
      const details = (error instanceof ApiClientError && error.details)
        ? JSON.stringify(error.details, null, 2)
        : undefined;
      const icon = iconForCode(code);
      errorDialog(`${icon} ${errorTitle || 'Error'}`, msg, details);
    }
    return null;
  }
}
